import React from 'react';
import { useToast, Toast } from '../../contexts/ToastContext';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void }> = ({ toast, onRemove }) => {
  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-400';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-400';
      case 'warning':
        return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-400';
      case 'info':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-400';
      default:
        return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-400';
    }
  };

  const getIcon = () => {
    const iconClasses = "h-5 w-5 flex-shrink-0";
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className={`${iconClasses} text-emerald-600 dark:text-emerald-400`} />;
      case 'error':
        return <XCircleIcon className={`${iconClasses} text-red-600 dark:text-red-400`} />;
      case 'warning':
        return <ExclamationTriangleIcon className={`${iconClasses} text-amber-600 dark:text-amber-400`} />;
      case 'info':
        return <InformationCircleIcon className={`${iconClasses} text-blue-600 dark:text-blue-400`} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-2xl border ${getToastStyles()} shadow-lg animate-slideInRight`}
    >
      {getIcon()}
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 hover:opacity-70 transition-opacity focus:ring-2 focus:ring-gray-400 focus:outline-none rounded"
        aria-label="Close notification"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-md w-[calc(100vw-2rem)] sm:w-full">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
};

export default ToastContainer;
